import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnDestroy,
  afterNextRender,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { TranslationService } from '../../../core/i18n/translation.service';
import { IssueDecisionStatus, IssueDto } from '../models/issue.models';
import { IssuesApi } from '../services/issues.api';

@Component({
  selector: 'app-issue-decision-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './issue-decision-modal.component.html',
  styleUrl: './issue-decision-modal.component.css',
})
export class IssueDecisionModalComponent implements OnDestroy {
  private readonly api = inject(IssuesApi);
  private readonly authService = inject(AuthService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly statusSelect = viewChild<ElementRef<HTMLSelectElement>>('statusSelect');
  private readonly dialogPanel = viewChild<ElementRef<HTMLElement>>('dialogPanel');
  private readonly previousBodyOverflow = this.document.body.style.overflow;

  protected readonly i18n = inject(TranslationService);
  readonly issue = input.required<IssueDto>();
  readonly closed = output<void>();
  readonly decided = output<IssueDto>();

  protected readonly form = this.formBuilder.nonNullable.group({
    status: ['' as IssueDecisionStatus | '', Validators.required],
    reason: [''],
  });
  protected readonly isSubmitting = signal(false);
  protected readonly submitError = signal('');
  protected readonly statuses: Array<{ value: IssueDecisionStatus; labelKey: string }> = [
    { value: 'in_progress', labelKey: 'issueList.status.in_progress' },
    { value: 'rejected', labelKey: 'issueList.status.rejected' },
    { value: 'resolved', labelKey: 'issueList.status.resolved' },
  ];

  constructor() {
    this.document.body.style.overflow = 'hidden';
    afterNextRender(() => this.statusSelect()?.nativeElement.focus({ preventScroll: true }));
    this.form.controls.status.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        if (status !== 'rejected' && this.form.controls.reason.hasError('required')) {
          this.form.controls.reason.setErrors(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.document.body.style.overflow = this.previousBodyOverflow;
  }

  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const panel = this.dialogPanel()?.nativeElement;
    if (!panel) {
      return;
    }

    const focusableElements = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      ),
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements.at(-1);
    if (!firstElement || !lastElement) {
      event.preventDefault();
      return;
    }

    const activeElement = this.document.activeElement;
    if (event.shiftKey && (activeElement === firstElement || !panel.contains(activeElement))) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  protected close(): void {
    if (!this.isSubmitting()) {
      this.closed.emit();
    }
  }

  protected async submit(): Promise<void> {
    this.form.markAllAsTouched();
    this.submitError.set('');

    const value = this.form.getRawValue();
    const reason = value.reason.trim();
    if (value.status === 'rejected' && !reason) {
      this.form.controls.reason.setErrors({ required: true });
    }
    if (this.form.invalid || !value.status) {
      return;
    }

    this.isSubmitting.set(true);
    try {
      const hasValidSession = await this.authService.ensureValidSession();
      if (!hasValidSession) {
        this.submitError.set(this.i18n.t('issueDecision.errors.sessionExpired'));
        return;
      }

      const updatedIssue = await firstValueFrom(
        this.api.updateIssueStatus(this.issue().id, {
          status: value.status,
          reason: reason || null,
        }),
      );
      this.decided.emit(updatedIssue);
    } catch (error) {
      const key =
        error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)
          ? 'issueDecision.errors.sessionExpired'
          : 'issueDecision.errors.submit';
      this.submitError.set(this.i18n.t(key));
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
